import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BackendStorageService } from './backend-storage.service';

export interface PlayHistoryEntry {
  trackId: string;
  trackUri: string;
  trackName: string;
  artistNames: string[];
  albumName: string;
  albumImage?: string; // Album cover image URL
  durationMs: number;
  progressMs?: number; // How much of the track was played (ms)
  playedAt: string; // ISO 8601 timestamp
  contextType?: 'playlist' | 'album' | 'artist' | 'track'; // Where it was played from
  contextUri?: string; // URI of the playlist/album/etc
  playSource: 'user' | 'autoplay' | 'radio'; // How the track was initiated
  playlistId?: string; // The playlist ID if played from a playlist
}

export interface PlayHistoryData {
  version: string; // For future migrations
  userId?: string; // For future backend integration
  entries: PlayHistoryEntry[];
  lastUpdated: string; // ISO 8601 timestamp
  totalPlays: number;
}

export interface PlayCountSummary {
  trackId: string;
  playCount: number;
  firstPlayed: string;
  lastPlayed: string;
  trackName: string;
  artistNames: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PlayHistoryService {
  private readonly STORAGE_KEY = 'spotify-play-history';
  private readonly MAX_ENTRIES = 10000; // Prevent storage from growing too large
  private readonly DATA_VERSION = '1.0';

  private playHistorySubject = new BehaviorSubject<PlayHistoryData>(this.getInitialData());
  public playHistory$ = this.playHistorySubject.asObservable();
  private initialized = false;

  constructor(private backendStorage: BackendStorageService) {
    // Load existing data on service initialization
    this.loadPlayHistory();
  }

  private getInitialData(): PlayHistoryData {
    return {
      version: this.DATA_VERSION,
      entries: [],
      lastUpdated: new Date().toISOString(),
      totalPlays: 0
    };
  }

  private async loadPlayHistory(): Promise<void> {
    try {
      // Try to load from backend storage
      const backendHistory = await this.backendStorage.getPlayHistory().toPromise();
      if (backendHistory && Array.isArray(backendHistory)) {
        // Transform backend format to our PlayHistoryData format
        const data: PlayHistoryData = {
          version: this.DATA_VERSION,
          entries: backendHistory,
          lastUpdated: new Date().toISOString(),
          totalPlays: backendHistory.length
        };
        this.playHistorySubject.next(data);
        this.initialized = true;
        return;
      }
    } catch (error) {
      console.warn('Failed to load play history from backend, trying localStorage:', error);
    }

    // Fallback to localStorage for migration or offline scenarios
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data: PlayHistoryData = JSON.parse(stored);
        
        // Handle version migrations if needed
        if (data.version !== this.DATA_VERSION) {
          console.log('Play history data version mismatch, migrating...');
          const migratedData = this.migrateData(data);
          await this.savePlayHistory(migratedData);
          this.playHistorySubject.next(migratedData);
        } else {
          this.playHistorySubject.next(data);
          // Try to migrate to backend storage
          this.migrateToBackend(data);
        }
      } else {
        // No data found, start fresh
        const initialData = this.getInitialData();
        this.playHistorySubject.next(initialData);
      }
    } catch (error) {
      console.error('Error loading play history:', error);
      // Reset to initial data if there's a parsing error
      const initialData = this.getInitialData();
      this.playHistorySubject.next(initialData);
    }
    
    this.initialized = true;
  }

  private async migrateToBackend(data: PlayHistoryData): Promise<void> {
    try {
      // Migrate entries one by one to maintain proper format
      for (const entry of data.entries.slice(0, 100)) { // Migrate only recent entries
        await this.backendStorage.addToPlayHistory(entry).toPromise();
      }
      // Clear localStorage after successful migration
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('Successfully migrated play history to backend');
    } catch (error) {
      console.warn('Play history migration to backend failed:', error);
    }
  }

  private migrateData(oldData: any): PlayHistoryData {
    // Future migration logic goes here
    // For now, just ensure the structure is correct
    return {
      version: this.DATA_VERSION,
      userId: oldData.userId,
      entries: Array.isArray(oldData.entries) ? oldData.entries : [],
      lastUpdated: new Date().toISOString(),
      totalPlays: oldData.totalPlays || oldData.entries?.length || 0
    };
  }

  private async savePlayHistory(data: PlayHistoryData): Promise<void> {
    if (this.initialized) {
      // Save to backend storage (entries are saved individually via recordPlay)
      // This method is mainly for local state management now
      return;
    }
    
    // Fallback to localStorage for backwards compatibility during initialization
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving play history to local storage:', error);
      // If storage is full, try to clean up old entries
      this.cleanupOldEntries();
    }
  }

  /**
   * Record a new play entry
   */
  recordPlay(
    trackId: string,
    trackUri: string,
    trackName: string,
    artistNames: string[],
    albumName: string,
    durationMs: number,
    contextType?: 'playlist' | 'album' | 'artist' | 'track',
    contextUri?: string,
    playSource: 'user' | 'autoplay' | 'radio' = 'user',
    playlistId?: string,
    albumImage?: string,
    progressMs?: number
  ): void {
    const currentData = this.playHistorySubject.value;

    // Extract playlistId from contextUri if not provided directly
    let resolvedPlaylistId = playlistId;
    if (!resolvedPlaylistId && contextType === 'playlist' && contextUri) {
      resolvedPlaylistId = contextUri.replace('spotify:playlist:', '');
    }

    const newEntry: PlayHistoryEntry = {
      trackId,
      trackUri,
      trackName,
      artistNames: [...artistNames], // Clone array
      albumName,
      albumImage,
      durationMs,
      progressMs,
      playedAt: new Date().toISOString(),
      contextType,
      contextUri,
      playSource,
      playlistId: resolvedPlaylistId
    };

    const updatedData: PlayHistoryData = {
      ...currentData,
      entries: [newEntry, ...currentData.entries], // Add to beginning (most recent first)
      lastUpdated: new Date().toISOString(),
      totalPlays: currentData.totalPlays + 1
    };

    // Limit storage size
    if (updatedData.entries.length > this.MAX_ENTRIES) {
      updatedData.entries = updatedData.entries.slice(0, this.MAX_ENTRIES);
    }

    // Update local state immediately
    this.playHistorySubject.next(updatedData);
    
    // Save to backend storage if initialized
    if (this.initialized) {
      this.backendStorage.addToPlayHistory(newEntry).subscribe({
        error: (error) => {
          console.error('Failed to save play history to backend:', error);
          // Fallback to localStorage on backend failure
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedData));
        }
      });
    } else {
      // Save to localStorage during initialization
      this.savePlayHistory(updatedData);
    }
    
    console.log('Recorded play:', trackName, 'by', artistNames.join(', '));
  }

  /**
   * Get play counts for tracks within a time window
   */
  getPlayCounts(timeWindowDays: number): Map<string, PlayCountSummary> {
    const currentData = this.playHistorySubject.value;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeWindowDays);
    const cutoffTime = cutoffDate.toISOString();

    const playCountMap = new Map<string, PlayCountSummary>();

    currentData.entries
      .filter(entry => entry.playedAt >= cutoffTime)
      .forEach(entry => {
        const existing = playCountMap.get(entry.trackId);
        
        if (existing) {
          existing.playCount++;
          existing.lastPlayed = entry.playedAt;
          if (entry.playedAt < existing.firstPlayed) {
            existing.firstPlayed = entry.playedAt;
          }
        } else {
          playCountMap.set(entry.trackId, {
            trackId: entry.trackId,
            playCount: 1,
            firstPlayed: entry.playedAt,
            lastPlayed: entry.playedAt,
            trackName: entry.trackName,
            artistNames: [...entry.artistNames]
          });
        }
      });

    return playCountMap;
  }

  /**
   * Get tracks that meet or exceed the maximum play count in the time window
   */
  getTracksExceedingPlayCount(timeWindowDays: number, maxPlayCount: number): string[] {
    const playCounts = this.getPlayCounts(timeWindowDays);
    return Array.from(playCounts.entries())
      .filter(([trackId, summary]) => summary.playCount >= maxPlayCount)
      .map(([trackId, summary]) => trackId);
  }

  /**
   * Get tracks that are below or equal to the maximum play count
   */
  getTracksWithinPlayCount(timeWindowDays: number, maxPlayCount: number): string[] {
    const playCounts = this.getPlayCounts(timeWindowDays);
    return Array.from(playCounts.entries())
      .filter(([trackId, summary]) => summary.playCount <= maxPlayCount)
      .map(([trackId, summary]) => trackId);
  }

  /**
   * Get recent tracks (for backwards compatibility with existing code)
   */
  getRecentTrackIds(timeWindowDays: number): string[] {
    const currentData = this.playHistorySubject.value;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeWindowDays);
    const cutoffTime = cutoffDate.toISOString();

    return currentData.entries
      .filter(entry => entry.playedAt >= cutoffTime)
      .map(entry => entry.trackId);
  }

  /**
   * Get play history statistics
   */
  getStatistics(): {
    totalPlays: number;
    uniqueTracks: number;
    averagePlaysPerTrack: number;
    oldestEntry?: string;
    newestEntry?: string;
  } {
    const currentData = this.playHistorySubject.value;
    const uniqueTrackIds = new Set(currentData.entries.map(e => e.trackId));

    return {
      totalPlays: currentData.totalPlays,
      uniqueTracks: uniqueTrackIds.size,
      averagePlaysPerTrack: uniqueTrackIds.size > 0 ? currentData.totalPlays / uniqueTrackIds.size : 0,
      oldestEntry: currentData.entries.length > 0 ? currentData.entries[currentData.entries.length - 1].playedAt : undefined,
      newestEntry: currentData.entries.length > 0 ? currentData.entries[0].playedAt : undefined
    };
  }

  /**
   * Clean up old entries to free storage space
   */
  private cleanupOldEntries(): void {
    const currentData = this.playHistorySubject.value;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const cutoffTime = thirtyDaysAgo.toISOString();

    const filteredEntries = currentData.entries.filter(entry => entry.playedAt >= cutoffTime);

    if (filteredEntries.length < currentData.entries.length) {
      const updatedData: PlayHistoryData = {
        ...currentData,
        entries: filteredEntries,
        lastUpdated: new Date().toISOString(),
        totalPlays: filteredEntries.length
      };

      this.savePlayHistory(updatedData);
      this.playHistorySubject.next(updatedData);
      
      console.log(`Cleaned up ${currentData.entries.length - filteredEntries.length} old play history entries`);
    }
  }

  /**
   * Clear all play history (for user privacy)
   */
  clearHistory(): void {
    const initialData = this.getInitialData();
    this.playHistorySubject.next(initialData);
    
    if (this.initialized) {
      this.backendStorage.clearPlayHistory().subscribe({
        next: () => {
          console.log('Play history cleared from backend');
        },
        error: (error) => {
          console.error('Failed to clear play history from backend:', error);
          // Clear localStorage as fallback
          localStorage.removeItem(this.STORAGE_KEY);
        }
      });
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
    
    console.log('Play history cleared');
  }

  /**
   * Export play history (for backup or migration)
   */
  exportHistory(): PlayHistoryData {
    return { ...this.playHistorySubject.value };
  }

  /**
   * Import play history (for restore or migration)
   */
  importHistory(data: PlayHistoryData): void {
    const migratedData = this.migrateData(data);
    this.savePlayHistory(migratedData);
    this.playHistorySubject.next(migratedData);
    console.log('Play history imported');
  }
}